import React, { useEffect, useState } from 'react';
import { Trash2, X, AlertCircle, RotateCcw, Edit2 } from 'lucide-react';
import { getReturPembelian, getReturPembelianById, createReturPembelian, updateReturPembelian, deleteReturPembelian, getPembelian, getInventoriSpareparts } from '../../api';
import { useAuth } from '../../AuthContext';

export default function ReturPembelian() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [pembelianList, setPembelianList] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null or retur object
  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), pembelian_id: '', alasan_retur: '',
    items: [{ sparepart_id: '', qty: 1, harga_satuan: 0, alasan: '' }]
  });

  const openModal = () => {
    setEditing(null);
    setError('');
    setForm({
      tanggal: new Date().toISOString().slice(0, 10), pembelian_id: '', alasan_retur: '',
      items: [{ sparepart_id: '', qty: 1, harga_satuan: 0, alasan: '' }]
    });
    setShowModal(true);
  };

  const openEdit = async (d) => {
    setError('');
    try {
      const res = await getReturPembelianById(d.id);
      const r = res.data;
      setEditing(r);
      setForm({
        tanggal: r.tanggal || '',
        pembelian_id: r.pembelian_id,
        alasan_retur: r.alasan_retur || '',
        items: (r.items || []).map(i => ({
          sparepart_id: i.sparepart_id,
          qty: i.qty,
          harga_satuan: i.harga_satuan,
          alasan: i.alasan || '',
        }))
      });
      setShowModal(true);
    } catch (err) {
      alert('Gagal memuat data retur');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setError('');
  };

  const load = () => {
    setLoading(true);
    Promise.all([getReturPembelian(), getPembelian(), getInventoriSpareparts({})])
      .then(([res, poRes, spRes]) => {
        setData(res.data);
        setPembelianList(poRes.data || []);
        setSpareparts(spRes.data || []);
      })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const handlePOCange = (poId) => {
    const po = pembelianList.find(p => p.id === Number(poId));
    if (po) {
      setForm(f => ({ ...f, pembelian_id: poId, supplier_name: po.supplier_name }));
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { sparepart_id: '', qty: 1, harga_satuan: 0, alasan: '' }] }));
  const updItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const delItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setError('');
    if (!editing && !form.pembelian_id) { setError('Pilih referensi pembelian'); return; }
    setSaving(true);
    try {
      const payload = {
        tanggal: form.tanggal, alasan_retur: form.alasan_retur,
        items: form.items.filter(i => i.sparepart_id).map(i => ({
          sparepart_id: Number(i.sparepart_id), qty: Number(i.qty),
          harga_satuan: Number(i.harga_satuan), alasan: i.alasan,
        }))
      };
      if (editing) {
        await updateReturPembelian(editing.id, payload);
      } else {
        await createReturPembelian({ ...payload, pembelian_id: Number(form.pembelian_id) });
      }
      closeModal(); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus retur ini?')) return;
    try { await deleteReturPembelian(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Retur Pembelian</h2>
            <p className="page-desc">{data.length} retur tercatat</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={openModal}>
              <RotateCcw size={16} /><span>Buat Retur</span>
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th><th>Tanggal</th><th>Supplier</th><th>PO Ref</th>
                <th>Qty</th><th>Total</th><th>Status</th><th style={{ width: 60 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {data.map(d => (
                <tr key={d.id}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td>{d.supplier_name || '-'}</td>
                  <td><code>{d.no_ref_po}</code></td>
                  <td>{d.total_qty}</td>
                  <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                  <td><span className={`badge ${d.status === 'Selesai' ? 'badge-success' : ''}`}>{d.status}</span></td>
                  <td style={{ display: 'flex', gap: 4 }}>
                    {hasMenuAction('inventori', 'edit') && (
                      <button className="btn-icon" style={{ color: '#fb6340' }} onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                    )}
                    {hasMenuAction('inventori', 'delete') && (
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Belum ada retur pembelian</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Retur Pembelian' : 'Buat Retur Pembelian'}</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid">
                <div className="form-group"><label>Tanggal</label><input type="date" value={form.tanggal} onChange={e => setForm({...form, tanggal: e.target.value})} /></div>
                <div className="form-group"><label>Referensi PO</label>
                  {editing ? (
                    <input type="text" value={pembelianList.find(p => p.id === Number(form.pembelian_id))?.no_ref || '#'} disabled style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem', background: '#f3f4f6' }} />
                  ) : (
                    <select value={form.pembelian_id} onChange={e => handlePOCange(e.target.value)}>
                      <option value="">-- Pilih --</option>
                      {pembelianList.map(p => <option key={p.id} value={p.id}>{p.no_ref} - {p.supplier_name}</option>)}
                    </select>
                  )}
                </div>
              </div>
              <div className="form-group"><label>Alasan Retur</label><textarea rows={2} value={form.alasan_retur} onChange={e => setForm({...form, alasan_retur: e.target.value})} placeholder="Alasan retur..." /></div>

              <h4 style={{ fontSize: '0.85rem', color: 'var(--gray-400)', textTransform: 'uppercase', margin: '16px 0 8px' }}>Item Retur</h4>
              {form.items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <select value={item.sparepart_id} onChange={e => updItem(idx, 'sparepart_id', e.target.value)} style={{ flex: 2, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem' }}>
                    <option value="">-- Pilih --</option>
                    {spareparts.map(s => <option key={s.id} value={s.id}>{s.code} - {s.name}</option>)}
                  </select>
                  <input type="number" value={item.qty} onChange={e => updItem(idx, 'qty', e.target.value)} style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem' }} />
                  <input type="number" value={item.harga_satuan} onChange={e => updItem(idx, 'harga_satuan', e.target.value)} style={{ width: 120, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem' }} />
                  <input type="text" value={item.alasan} onChange={e => updItem(idx, 'alasan', e.target.value)} placeholder="Alasan" style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: '0.82rem' }} />
                  <button className="btn-icon btn-danger" onClick={() => delItem(idx)} disabled={form.items.length === 1}><X size={14} /></button>
                </div>
              ))}
              <button className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '6px 12px' }} onClick={addItem}>+ Tambah Item</button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : <><RotateCcw size={16} /> Proses Retur</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
